/**
 * PlayerPageMobileFabView - floating bottom-right player FAB (Notion 8.6, SE-5083)
 *
 * Mobile sibling of the desktop PlayerHeroCtaCluster. Renders a true
 * floating action button anchored bottom-right above the bottom nav. The
 * `+` rotates to `✕` when expanded and three labelled action pills stack
 * upward with a staggered entrance:
 *   1. Update list / Add to watchlist (conditional on watchlist membership)
 *   2. Write a note
 *   3. Create a report
 *
 * When expanded an invisible full-screen backdrop sits behind the stack so a
 * tap outside collapses it (mirrors the PopoverBackdrop pattern). Each FAB
 * button meets the >=44pt iOS / >=48dp Android tap target via `size-14`
 * (56px) for the FAB and `min-h-12` (48px) for the action pills.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { Plus } from "lucide-react-native";
import { memo } from "react";
// design-system: rn-primitive waiver — Animated transforms (rotation + stagger)
import { Animated } from "react-native";

import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

/**
 * Gap between the FAB's bottom edge and the bottom of its positioning context,
 * in px. Mirrors the `bottom-4` on the FAB cluster below — the two must move
 * together.
 */
const FAB_BOTTOM_INSET_PX = 16;

/**
 * The collapsed FAB's diameter, in px. Mirrors the `size-14` on the FAB
 * Pressable below — the two must move together.
 */
const FAB_DIAMETER_PX = 56;

/**
 * Vertical space the collapsed FAB claims above the bottom of the screen.
 *
 * SE-6910: the FAB floats over scroll content rather than displacing it, so a
 * row control that comes to rest inside this band is painted underneath and its
 * taps are swallowed by the FAB. Measured on Android (run 31594137962), the
 * notes list's remove control landed at `[942,1974][1005,2037]`, entirely
 * inside the FAB's `[881,1959][1028,2106]`, and the tap opened the FAB menu.
 *
 * Scroll surfaces that render the FAB reserve at least this much at the end of
 * their content — see {@link FAB_SAFE_AREA_CLASS}.
 */
export const FAB_FOOTPRINT_PX = FAB_BOTTOM_INSET_PX + FAB_DIAMETER_PX;

/**
 * Height class a scroll surface appends to reserve {@link FAB_FOOTPRINT_PX}.
 *
 * `h-20` (80px) rather than a padding class: the closed spacing scale tops out
 * at 64px, which is short of the 72px footprint, while the sizing ladder's next
 * rung at or above it is 80px. Expressed as a spacer height for that reason,
 * not as a stylistic preference.
 */
export const FAB_SAFE_AREA_CLASS = "h-20";

/** Animated style for a single staggered action pill (opacity + translateY) */
export interface FabActionAnimatedStyle {
  readonly opacity: Animated.AnimatedInterpolation<string | number>;
  readonly transform: readonly {
    readonly translateY: Animated.AnimatedInterpolation<string | number>;
  }[];
}

/** A single expandable FAB action */
export interface FabAction {
  /** Stable key + testID for the action */
  readonly testID: string;
  /** Visible pill label */
  readonly label: string;
  /** Press handler — opens the corresponding existing flow */
  readonly onPress: () => void;
  /** Pre-computed animated entrance style for the pill */
  readonly animatedStyle: FabActionAnimatedStyle;
}

/**
 * Props for PlayerPageMobileFabView
 */
export interface PlayerPageMobileFabViewProps {
  /** Whether the FAB is expanded (actions visible, icon shows ✕) */
  readonly isExpanded: boolean;
  /** Toggle handler for the main FAB button */
  readonly onToggle: () => void;
  /** Collapse handler for the tap-outside backdrop */
  readonly onBackdropPress: () => void;
  /** Action pills rendered above the FAB, top-to-bottom */
  readonly actions: readonly FabAction[];
  /** Pre-computed animated rotation style for the +/✕ icon */
  readonly iconStyle: {
    readonly transform: readonly {
      readonly rotate: Animated.AnimatedInterpolation<string | number>;
    }[];
  };
  /** Pre-computed a11y state for the FAB (avoids re-allocating per render) */
  readonly fabAccessibilityState: { readonly expanded: boolean };
}

/**
 * PlayerPageMobileFabView renders the floating FAB and its expandable actions
 * @param props - View props
 * @param props.isExpanded - Whether the FAB is expanded
 * @param props.onToggle - Toggle handler for the main FAB button
 * @param props.onBackdropPress - Collapse handler for the tap-outside backdrop
 * @param props.actions - Action pills rendered above the FAB
 * @param props.iconStyle - Pre-computed animated rotation style for the +/✕ icon
 * @param props.fabAccessibilityState - Pre-computed expanded a11y state for the FAB
 * @returns Absolutely-positioned FAB cluster anchored bottom-right
 */
const PlayerPageMobileFabView = ({
  isExpanded,
  onToggle,
  onBackdropPress,
  actions,
  iconStyle,
  fabAccessibilityState,
}: PlayerPageMobileFabViewProps) => (
  <>
    {/* Tap-outside backdrop — only mounted while expanded so it never
        swallows touches on the page behind a collapsed FAB. */}
    {isExpanded ? (
      <Pressable
        testID={TEST_ID_PLAYER_DETAIL.MOBILE_FAB_BACKDROP}
        accessibilityRole="button"
        accessibilityLabel="Close actions"
        onPress={onBackdropPress}
        className="absolute inset-0 z-10"
      />
    ) : null}

    {/* FAB cluster: actions stack upward above the FAB, anchored bottom-right.
        The tab navigator reserves its own space (no absolute overlay), so the
        screen already stops at the nav's top edge — only a small visual gap is
        needed here (bottom-4 = 16px), matching the scouting AddToListFab. */}
    <Box className="absolute bottom-4 right-5 z-20 items-end gap-2">
      {isExpanded
        ? actions.map(action => (
            // eslint-disable-next-line no-restricted-syntax -- Animated.View staggered pill entrance (opacity + translateY interpolation); non-atomizable
            <Animated.View key={action.testID} style={action.animatedStyle}>
              <Pressable
                testID={action.testID}
                accessibilityRole="button"
                accessibilityLabel={action.label}
                onPress={action.onPress}
                className="min-h-12 flex-row items-center justify-center rounded-full bg-surface-raised px-4 shadow-soft-2"
              >
                <Text
                  variant="body-strong"
                  className="text-content-primary"
                  numberOfLines={1}
                >
                  {action.label}
                </Text>
              </Pressable>
            </Animated.View>
          ))
        : null}

      <Pressable
        testID={TEST_ID_PLAYER_DETAIL.MOBILE_FAB}
        accessibilityRole="button"
        accessibilityLabel={isExpanded ? "Close actions" : "Open actions"}
        accessibilityState={fabAccessibilityState}
        onPress={onToggle}
        className="size-14 items-center justify-center rounded-full bg-accent-primary shadow-soft-2"
      >
        {/* eslint-disable-next-line no-restricted-syntax -- Animated.View rotation transform for +/✕ FAB icon; non-atomizable */}
        <Animated.View style={iconStyle}>
          <Icon as={Plus} size="lg" className="text-content-on-accent" />
        </Animated.View>
      </Pressable>
    </Box>
  </>
);

PlayerPageMobileFabView.displayName = "PlayerPageMobileFabView";

export default memo(PlayerPageMobileFabView);
